import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import api from "@/services/api";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, ChevronLeft, ChevronRight, PanelRightOpen, CheckCircle, Trash2, RotateCcw } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";

const AVATAR_COLORS = ["#6d28d9", "#4f46e5", "#0e7490", "#b45309", "#15803d", "#be185d"];
const avatarColor = (s: string) => AVATAR_COLORS[[...(s || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const initials = (name: string) => (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

interface FailedRow {
  id: string;
  user_id: string;
  application_id: string | null;
  test_id: string;
  full_name: string;
  email: string;
  test_name: string;
  score: number | null;
  total_marks: number | null;
  percentage: number | null;
  failed_at: string | null;
}

const PAGE_SIZE = 10;

const FailedStudents = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<FailedRow[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [toDelete, setToDelete] = useState<FailedRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toReassign, setToReassign] = useState<FailedRow | null>(null);
  const [reassigning, setReassigning] = useState(false);

  const fetchFailed = async () => {
    const { data: assignments } = await supabase
      .from("exam_assignments")
      .select("*")
      .eq("status", "failed")
      .order("completed_at", { ascending: false });

    if (!assignments || assignments.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }

    const userIds = [...new Set(assignments.map((a) => a.user_id))];
    const { data: profiles } = await supabase
      .from("profiles")
      .select("user_id, full_name, email")
      .in("user_id", userIds);

    const profMap = new Map<string, { full_name: string; email: string }>();
    profiles?.forEach((p) => profMap.set(p.user_id, { full_name: p.full_name, email: p.email }));

    const merged: FailedRow[] = assignments.map((a) => ({
      id: a.id,
      user_id: a.user_id,
      application_id: a.application_id,
      test_id: a.think_exam_test_id,
      full_name: profMap.get(a.user_id)?.full_name || "—",
      email: profMap.get(a.user_id)?.email || a.think_exam_candidate_email || "—",
      test_name: a.think_exam_test_name || a.think_exam_test_id || "—",
      score: a.score,
      total_marks: a.total_marks,
      percentage: a.percentage,
      failed_at: a.completed_at || a.updated_at,
    }));

    setRows(merged);
    setLoading(false);
  };

  const handleReassign = async () => {
    if (!toReassign) return;
    if (!toReassign.application_id) {
      toast({ title: "Cannot reassign", description: "This exam has no linked application. Reassign from the student page instead.", variant: "destructive" });
      setToReassign(null);
      return;
    }
    setReassigning(true);
    try {
      await api.post(`/think-exam/reassign/${toReassign.application_id}`, { examAssignmentId: toReassign.id });
      setRows((prev) => prev.filter((r) => r.id !== toReassign.id));
      toast({
        title: "Exam reassigned",
        description: `A new attempt was created for ${toReassign.email}. Now reset this test for the candidate in the ThinkExam admin panel so the previous attempt is cleared.`,
      });
      setToReassign(null);
    } catch (e: any) {
      toast({ title: "Reassign failed", description: e.message, variant: "destructive" });
    } finally {
      setReassigning(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/think-exam/assignment/${toDelete.id}`);
      setRows((prev) => prev.filter((r) => r.id !== toDelete.id));
      toast({ title: "Record deleted", description: `Removed ${toDelete.test_name} for ${toDelete.email}` });
      setToDelete(null);
    } catch (e: any) {
      toast({ title: "Delete failed", description: e.message, variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchFailed();
    // Pull-on-load: sync any pending results from ThinkExam, then refresh the list.
    api.post("/think-exam/sync-pending").then(() => fetchFailed()).catch(() => {});
    // Live updates: re-pull whenever any exam_assignments row changes.
    const channel = supabase
      .channel("failed-exam-assignments")
      .on("postgres_changes", { event: "*", schema: "public", table: "exam_assignments" }, () => fetchFailed())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = rows.filter(
    (r) =>
      r.full_name.toLowerCase().includes(search.toLowerCase()) ||
      r.email.toLowerCase().includes(search.toLowerCase()) ||
      r.test_name.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search]);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader
          title="Failed Students"
          badge={<span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">{filtered.length} failed</span>}
          actions={
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search name, email or exam..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-11 rounded-xl pl-10 pr-9"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
            </div>
          }
        />

        {/* Table */}
        <Card className="glass-premium overflow-hidden rounded-2xl">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="w-12 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">S/No</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Name / Email</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Exam</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Score</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Failed On</TableHead>
                      <TableHead className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginated.map((row, idx) => (
                      <TableRow key={row.id} className="border-border hover:bg-muted/40 transition-colors">
                        <TableCell className="text-xs text-muted-foreground tabular-nums">{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-bold text-white" style={{ background: avatarColor(row.full_name) }}>
                              {initials(row.full_name)}
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-foreground">{row.full_name}</p>
                              <p className="text-xs text-muted-foreground">{row.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{row.test_name}</TableCell>
                        <TableCell>
                          {row.score != null ? (
                            <span className="text-sm">
                              <span className="font-medium text-red-600">{row.score}</span>
                              <span className="text-muted-foreground">/{row.total_marks}</span>
                              {row.percentage != null && <span className="ml-1.5 text-xs text-muted-foreground">({row.percentage}%)</span>}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {row.failed_at ? format(new Date(row.failed_at), "MMM d, yyyy") : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              title="View Student (reassign exam)"
                              className="h-8 w-8 p-0 border-border/60 hover:bg-primary hover:text-white hover:border-primary transition-all duration-200"
                              onClick={() => navigate(`/growlearn/students/${row.user_id}`)}
                            >
                              <PanelRightOpen className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              title="Reassign exam (allow retake)"
                              className="h-8 w-8 p-0 border-border/60 text-amber-600 hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-all duration-200"
                              onClick={() => setToReassign(row)}
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              title="Delete record"
                              className="h-8 w-8 p-0 border-border/60 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-200"
                              onClick={() => setToDelete(row)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                          <CheckCircle className="mx-auto h-8 w-8 text-success/40 mb-2" />
                          No failed students
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
            {!loading && filtered.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
                <p className="text-xs text-muted-foreground">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} failed exams
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="h-8 w-8 flex items-center justify-center rounded-md text-xs font-semibold gradient-warm text-white">
                    {page}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={!!toReassign} onOpenChange={(open) => !open && setToReassign(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reassign this exam?</AlertDialogTitle>
            <AlertDialogDescription>
              This creates a fresh attempt for{" "}
              <span className="font-medium text-foreground">{toReassign?.email}</span> ({toReassign?.test_name}) so the
              student sees the exam link again.
              <br /><br />
              <span className="font-medium text-amber-700">Important:</span> after this, you must reset this test for the
              candidate in the <span className="font-medium">ThinkExam admin panel</span> so their previous attempt is
              cleared — otherwise ThinkExam won't let them retake it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reassigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleReassign(); }}
              disabled={reassigning}
              className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
            >
              {reassigning ? "Reassigning..." : "Reassign"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the failed exam record for{" "}
              <span className="font-medium text-foreground">{toDelete?.email}</span> ({toDelete?.test_name}).
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default FailedStudents;
